import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MailWarning, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Subtle "outbound email is healthy" pill for the admin overview.
 *
 * Hits ``/commissions/graph-config`` (already admin-scoped) and lights up
 * one of three states:
 *   - Loading — dim grey, spinner
 *   - Healthy — emerald, cheerful "Email healthy"
 *   - Misconfigured — amber, clicks straight to Settings → Email to fix
 *
 * Deliberately compact — sits inline next to the header chip, not a
 * banner. The idea is admins glance at it every time they load Overview
 * and only *notice* it when something's wrong.
 */
export default function EmailHealthPill() {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/commissions/graph-config");
        if (!cancelled) setState({ loading: false, configured: !!data?.configured, sender: data?.sender || "" });
      } catch (_) {
        if (!cancelled) setState({ loading: false, configured: false, sender: "" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <span
        data-testid="email-health-pill"
        data-status="loading"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-700 bg-slate-800/50 text-[10px] normal-case tracking-normal font-medium text-slate-400"
      >
        <Loader2 className="w-3 h-3 animate-spin" /> Checking email
      </span>
    );
  }

  if (state.configured) {
    return (
      <span
        data-testid="email-health-pill"
        data-status="healthy"
        title={state.sender ? `Outbound via ${state.sender} (Microsoft Graph)` : "Microsoft Graph is configured"}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[10px] normal-case tracking-normal font-medium text-emerald-300"
      >
        <Mail className="w-3 h-3" /> Email healthy
      </span>
    );
  }

  return (
    <Link
      to="/admin/settings?tab=email"
      data-testid="email-health-pill"
      data-status="misconfigured"
      title="Microsoft Graph isn't configured — customer emails, payout PDFs, and stage automations will fail silently until this is fixed."
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-[10px] normal-case tracking-normal font-medium text-amber-300 hover:bg-amber-500/20 hover:border-amber-500/60 transition-colors"
    >
      <MailWarning className="w-3 h-3" /> Email not configured &mdash; fix
    </Link>
  );
}
